import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Shield, 
  Award, 
  Globe, 
  Users, 
  ChevronRight, 
  ChevronLeft,
  ArrowRight,
  Building
} from "lucide-react";
import HQ from '../../src/assets/images/HQ_Logo.svg'
import Micromax from '../../src/assets/images/MMX.png'
export default function Partners() {
  const containerRef = useRef(null);
  const [activePartner, setActivePartner] = useState('hq');
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, -300]);

  const partners = {
    hq: {
      name: "Huaqin Technology",
      image:HQ,
      path:"https://en.huaqin.com/about/company",
      tagline: "Innovation Through Excellence",
      description: "Founded in 2005 and based in Shanghai, Huaqin Technology Co., Ltd. ('Huaqin') is a world-leading enterprise specialized in smart products. As part of its mission to 'Improve People's Communication and Life', Huaqin offers end-to-end product development, manufacturing, and operation of software, hardware, and systems to global tech companies. With a large customer base in more than 100 countries and regions worldwide, Huaqin provides global consumers with hundreds of millions of intelligent products including mobile phones, tablets, laptops, smart wearables, AIoT products, IDC products, and automotive electronics.",
      stats: [
        { label: "2023 Operating Revenue", value: "$12050 Million" },
        { label: "2024 Fortune China 500", value: "231th" },
        { label: "2024 Top 500 Chinese Enterprises", value: "289th" },
        { label: "2024 Top 100 Players of China's Electronics and Information Industry", value: "20th" }
      ],
      achievements: [
        { 
          year: "2005",
          title: "Foundation Era",
          description: "This marks the establishment and beginning of Huaqin in China"
        },
        {
          year: "2011-2013",
          title: "Global Leadership & Expansion",
          description: "Marks their entry into smartphones, manufacturing expansion, and achieving market leadership"
        },
        {
          year: "2015-2016",
          title: "Market Dominance & Product Diversification",
          description: "Represents their US market entry and expansion into laptops while maintaining top position"
        },
        {
          year: "2017-2018",
          title: "Innovation & Infrastructure Growth",
          description: "Focuses on R&D expansion and new manufacturing facilities"
        },
        {
          year: "2019",
          title: "Global Manufacturing & Investment",
          description: "Highlights their overseas expansion and significant funding rounds"
        },
        {
          year: "2020-21",
          title: "Software & Automotive Innovation",
          description: "Reflects their expansion into software and automotive sectors, plus establishment of new business divisions"
        },
        {
          year: "2022",
          title: "Global R&D & Automotive Production",
          description: "Captures the start of their global R&D headquarters construction and automotive electronics production"
        },
        {
          year: "2023",
          title: "Public Listing & R&D Enhancement",
          description: "Highlights their significant milestone of going public on the Shanghai Stock Exchange and R&D expansion"
        }

      ]
    },
    mmx: {
      name: "Micromax Informatics",
      image:Micromax,
      tagline: "Shaping Tomorrow's Technology",
      description: "Micromax Informatics is India’s home grown consumer electronics brand. Over the past decade, Micromax has pioneered the democratization of technology in India - by offering affordable innovations through their product offerings and removing barriers for large scale adoption of advanced technologies. Micromax is a brand which is close to the heart of the youth, and celebrates the vibrancies of life and empowerment.",
      stats: [
        { label: "Industry Partners", value: "300+" },
        { label: "Projects Delivered", value: "1000+" },
        { label: "Countries Served", value: "40+" },
        { label: "R&D Centers", value: "8" }
      ],
      achievements: [
        {
          year: "2008-10",
          title: "Mobile Market Entry",
          description: "A pivotal period marking the company's strategic entrance into the mobile phone industry, achieving significant sales volume of 100,000 handsets monthly, establishing their initial foothold in the mobile market."
        },
        {
          year: "2010-12",
          title: "Product Diversification",
          description: " A transformative phase where the company expanded beyond mobile phones, strategically diversifying into Tablet PCs and LED TVs while simultaneously developing their smartphone vertical, demonstrating their commitment to broader consumer electronics."
        },
        {
          year: "2012-14",
          title: "Global Brand Recognition",
          description: "A milestone period marked by achieving status as the 10th largest mobile brand worldwide, enhanced by celebrity endorsement with Hugh Jackman for the Canvas range, and successfully repositioning as a Global Indian brand in their domestic market."
        },
        {
          year: "2015-17",
          title: "Smart Technology Evolution",
          description: "A period of technological advancement focused on smart connectivity, highlighted by the launch of Smart TVs and the introduction of their online YU brand, showing their adaptation to changing consumer preferences in the digital age."
        }
      ]
    }
  };

  return (
    <div ref={containerRef} className="relative bg-gradient-to-b from-orange-50/30 to-white">
      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <motion.div 
          style={{ y }}
          className="absolute inset-0"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 to-white/80 mix-blend-multiply" />
          
        </motion.div>
        
        <div className="relative z-10 container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-7xl font-bold tracking-tight mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-yellow-500">
                Strategic Partnerships
              </span>
            </h1>
            <p className="text-lg md:text-2xl text-gray-700 mb-8">
              
Combining legacy, innovation and best in class manufacturing expertise under one roof

            </p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <button 
                onClick={() => setActivePartner('hq')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all ${
                  activePartner === 'hq' 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-white/80 text-gray-700 hover:bg-white'
                }`}
              >
                Huaqin Technology
              </button>
              <button
                onClick={() => setActivePartner('mmx')}
                className={`px-6 py-3 rounded-full text-sm font-semibold transition-all ${
                  activePartner === 'mmx'
                    ? 'bg-orange-600 text-white'
                    : 'bg-white/80 text-gray-700 hover:bg-white'
                }`}
              >
                MMX (Micromax Smart Phone Founder)
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Partner Overview */}
      <section className="py-24 bg-gradient-to-b from-orange-50/30 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-7xl mx-auto"
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">
                  {partners[activePartner].name}
                </h2>
                <p className="text-2xl text-orange-600 font-semibold mb-6">
                  {partners[activePartner].tagline}
                </p>
                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                  {partners[activePartner].description}
                </p>
                <Link to={partners[activePartner].path}>
                <button className="inline-flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors">
                  Learn More <ArrowRight className="w-4 h-4" />
                </button>
                </Link>
              </div>
              <div className="m-20 relative aspect-square rounded-2xl overflow-hidden">
                <img
                  src={partners[activePartner].image}
                  alt={partners[activePartner].name}
                  className="absolute inset-0 w-full object-cover "
                />
               
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      

      

     
      
    </div>
  );
}